import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type UseMetricChartResult, useMetricChart } from '@/components/charts/generic';
import type { SwimSpeedDataPoint, ThresholdRange } from '@/domain/behavior';
import { parseLocale } from '@/utils/localeFormatters';

import { toBehaviorContext } from '../contextSeries/contextSeries';

import { buildSwimSpeedSpec } from './swimSpeedChartSpec';

interface UseSwimSpeedChartDataParams {
    swimSpeedDataPoints: SwimSpeedDataPoint[];
    swimSpeedThreshold: ThresholdRange;
}

export interface UseSwimSpeedChartDataReturn {
    chart: UseMetricChartResult<'line'>;
    hasOxygenData: boolean;
    /** The radio group's value: 'temperature', 'depth' or 'oxygen'. */
    selectedRightAxisValue: string;
    setSelectedRightAxisValue: (value: string) => void;
}

/** The swim speed page's chart, with the reading on its right axis picked by a radio group. */
export function useSwimSpeedChartData({
    swimSpeedDataPoints,
    swimSpeedThreshold,
}: UseSwimSpeedChartDataParams): UseSwimSpeedChartDataReturn {
    const { t, i18n } = useTranslation('behaviorModule');
    const [choice, setChoice] = useState('temperature');

    const hasOxygenData = useMemo(
        () => swimSpeedDataPoints.some((point) => point.oxygen),
        [swimSpeedDataPoints]
    );
    const context = toBehaviorContext(choice, hasOxygenData);

    const spec = useMemo(
        () =>
            buildSwimSpeedSpec({
                points: swimSpeedDataPoints,
                threshold: swimSpeedThreshold,
                t,
                locale: parseLocale(i18n.language),
                context,
            }),
        [swimSpeedDataPoints, swimSpeedThreshold, t, i18n.language, context]
    );

    return {
        chart: useMetricChart(spec),
        hasOxygenData,
        selectedRightAxisValue: context,
        setSelectedRightAxisValue: setChoice,
    };
}
